/**
 * Shop Checkout Service — the axoix.com half of the cross-domain payment hop.
 *
 * Why this exists: Razorpay declines every AXOIX-owned payment opened on a
 * `<tenant>.axoix.io` origin, because those subdomains are not registered on
 * the AXOIX merchant account and cannot be — each one is a tenant's own space
 * where *their* merchant transacts (BR-115). axoix.com IS registered, so the
 * tenant's dashboard hands the purchase over and the sheet opens here.
 *
 * The token is the authorisation. POC mints a session server-side through
 * `POST /api/shop/checkout-session` (first-party, X-Internal-Key), which is the
 * gate: it matches the tenant, prices the cart and creates the Razorpay
 * order/subscription BEFORE any token exists. This page only ever exchanges it.
 *
 * Two separate tokens, deliberately:
 *   - `session_token`  — burned on exchange, single-use, ~10 min TTL
 *   - `verify_token`   — minted BY the exchange, carries the tenant, and is
 *                        what proves whose payment this is on the way back
 *
 * They are distinct because the session token is spent the moment the page
 * loads, so it cannot also serve at verify time. Never send `tenant_id` from
 * here: the mothership's verify endpoints accept `tenant_id` OR `verify_token`
 * and the browser must not be the thing naming a tenant.
 */

import apiClient from "./client";

/** Which of the three AXOIX-owned flows this session is for. */
export type CheckoutSessionKind = "cart" | "subscription" | "tier_upgrade";

/**
 * One priced line in the confirm screen. `name` and `price` are present for
 * cart lines (the mothership runs them through calculate_total) and for the
 * two subscription kinds; nothing here is trusted for the charge itself —
 * Razorpay is opened against the order/subscription the mothership already
 * created, and `amount` below is only what we display.
 */
export interface CheckoutSessionItem {
  type: string;
  id: string;
  name?: string;
  price?: number;
  quantity?: number;
  billing_cycle?: string;
}

/**
 * The exchange response, field for field as `shop.py` returns it.
 *
 * `order_id` is set for kind="cart"; `subscription_id` for "subscription" and
 * "tier_upgrade". Exactly one of the two is present, which is what picks the
 * Razorpay mode and the verify endpoint.
 */
export interface CheckoutSessionData {
  subdomain: string;
  company_name?: string | null;
  kind: CheckoutSessionKind;
  /** Proof of tenant for the verify call. Opaque — never parsed here. */
  verify_token: string;
  items: CheckoutSessionItem[];
  /** In paisa, GST-inclusive, as the mothership computed it. */
  amount: number;
  currency: string;
  key_id: string;
  order_id?: string | null;
  subscription_id?: string | null;
  description?: string | null;
  /**
   * Where the tenant goes when this is over. Validated at ISSUE time to https
   * on *.axoix.io (`shop.py:_validate_return_url`) — so it is safe to use as a
   * redirect target, and it is the ONLY value that may be used as one. A
   * return_url read from our own query string would make this page an open
   * redirect.
   */
  return_url: string;
}

/** Raised when the token is missing, expired, or already spent. */
export class CheckoutSessionError extends Error {
  readonly expired: boolean;

  constructor(message: string, expired: boolean) {
    super(message);
    this.name = "CheckoutSessionError";
    this.expired = expired;
  }
}

/**
 * Exchange a single-use session token for the payment parameters.
 *
 * The only mothership endpoint this page calls before payment. The subdomain
 * comes out of the token — we never send one, so a hand-edited URL cannot open
 * a checkout against someone else's tenant.
 */
export const exchangeCheckoutSession = async (
  sessionToken: string
): Promise<CheckoutSessionData> => {
  try {
    const response = await apiClient.post<CheckoutSessionData>(
      "/api/shop/checkout-session/exchange",
      { session_token: sessionToken }
    );
    return response.data;
  } catch (error) {
    // The client interceptor rejects with ApiError, not an AxiosError, and it
    // REPLACES the body's detail with a generic string on 404 (client.ts:90).
    // So the mothership's own wording is not available here — we supply the
    // equivalent message ourselves rather than showing "The requested resource
    // was not found" to someone who just tried to pay.
    const err = error as { status?: number; message?: string };

    // 404 is expired, already-used and never-existed alike — the mothership
    // answers them identically on purpose, since distinguishing them would
    // tell a caller which tokens are real. We keep that opacity.
    if (err.status === 404) {
      throw new CheckoutSessionError(
        "This checkout link has expired or has already been used. Please start again from your dashboard.",
        true
      );
    }
    throw new CheckoutSessionError(
      err.message || "We could not open this checkout. Please try again from your dashboard.",
      false
    );
  }
};

interface VerifyResult {
  success: boolean;
  message?: string;
}

/**
 * Confirm a one-time cart payment (kind="cart").
 * Routes to the shop's own verify, which activates the purchased items.
 */
export const verifyShopPayment = async (payload: {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  verify_token: string;
}): Promise<VerifyResult> => {
  const response = await apiClient.post<VerifyResult>("/api/shop/verify-payment", payload);
  return response.data;
};

/**
 * Confirm the first charge of a recurring addon/vertical (kind="subscription").
 */
export const verifyAddonSubscription = async (payload: {
  razorpay_subscription_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  verify_token: string;
}): Promise<VerifyResult> => {
  const response = await apiClient.post<VerifyResult>(
    "/api/shop/verify-addon-subscription",
    payload
  );
  return response.data;
};

/**
 * Confirm the first charge of a tier upgrade (kind="tier_upgrade").
 *
 * This one lives in billing, not shop — the mandate is a tier subscription and
 * `checkout-session` wrote `settings.pending_subscription` for it at mint time,
 * which is what this endpoint reads to know what was bought.
 */
export const verifyTierSubscription = async (payload: {
  razorpay_subscription_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  verify_token: string;
}): Promise<VerifyResult> => {
  const response = await apiClient.post<VerifyResult>(
    "/api/billing/verify-subscription",
    payload
  );
  return response.data;
};

/**
 * Build the redirect back to the tenant's dashboard.
 *
 * `returnUrl` must be the one from the exchange response and nothing else.
 * Preserves any query string the dashboard put there itself.
 */
export const buildReturnUrl = (
  returnUrl: string,
  status: "success" | "failed" | "cancelled"
): string => {
  const separator = returnUrl.includes("?") ? "&" : "?";
  return `${returnUrl}${separator}status=${status}`;
};

const shopCheckoutService = {
  exchangeCheckoutSession,
  verifyShopPayment,
  verifyAddonSubscription,
  verifyTierSubscription,
  buildReturnUrl,
};

export default shopCheckoutService;
